import { useChatbotRegistration } from '@/common/ai/ChatbotMgrContext';
import { ExportButton } from '@/common/buttons/ExportButton';
import { SubButton } from '@/common/buttons/SubButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { ContentBox } from '@/common/layout/ContentBox';
import { DateUtils } from '@/common/utils/DateUtils';
import { EntityUtils } from '@/common/utils/EntityUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AddButton } from '../../buttons/AddButton';
import { DEFAULT_GRID_OPTIONS, Grid } from '../../grid/Grid';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { usePrincipal } from '../../principal/PrincipalContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { ExprUtils } from '../../utils/ExprUtils';
import { StringUtils } from '../../utils/StringUtils';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { useTab } from '../entityTabs/TabContext';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { SearchBox } from '../entityTitle/SearchBox';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { Mode } from '../enumerations/Mode';

export const EntityList = ({
  id,

  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Optional Modifications to EntityList
  augFieldDefs,
  augRowsReq,
  clientSideRowData,

  // Optional Modifications to the Grid
  gridOptions = {},
  events = {},

  // Optional augmentation of buttons in main lists
  augButtons,
  // Optional override of buttons for lists in a Tab
  augTabButtons,

  // Optional external refresh trigger (e.g. from a cross-entity subscription)
  refreshTrigger: refreshTriggerProp,

  // Optional lever for showing the title and banner
  showTitle = true,

  // Optional lever for showing the built-in Export button
  showExport = true,

  // Ref
  ref,
}) => {
  const { tabLabel } = useTab();

  if (tabLabel && tabLabel !== 'Info' && !entity) {
    throw new Error(`An Entity needs to be provided to the EntityList in tab ${tabLabel}. This is a development error`);
  }

  // Module and Entity
  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  // Context
  const rsrc = useRsrc();
  const { hist } = useEntityMgr();

  const { getFieldRoleSet, hasRole } = usePrincipal();
  const { navigateTo, navState, mode } = useNavState();
  const { openDialog } = useDialog();

  // Ref
  const gridRef = useRef(null);

  // State
  const [exportReq, setExportReq] = useState();
  const [fieldDefs, setFieldDefs] = useState();
  const [entityDef, setEntityDef] = useState();

  // API to Parent
  useImperativeHandle(ref, () => {
    return {
      fieldDefs,
      getContextMenuItems,
      gridRef: gridRef,
    };
  }, [fieldDefs]);

  // Describe this list to the chatbot from the same request the export uses, so the chatbot can
  // re-fetch exactly what the grid shows. Client-side rows never set exportReq and stay unavailable.
  const chatbotName = entityDef && `${entityDef.displayName} list`;
  let chatbotDescription = null;
  let chatbotDataFetchParams = null;
  if (entityDef) {
    if (clientSideRowData) {
      chatbotDescription = 'A grid whose data is currently unavailable to the chatbot.';
    } else if (exportReq) {
      const { startRow, endRow, ...rowsReq } = exportReq.rowsReq;
      chatbotDescription =
        'A grid of ' +
        entityDef.displayName +
        (hist ? ' versions for one record' : ' records') +
        (rowsReq.expr ? ', filtered by the user' : ', unfiltered') +
        '.';
      // fieldToDisplayName lets the chatbot show rows with the column names the user sees
      const fieldToDisplayName = Object.fromEntries(
        fieldDefs.filter((fieldDef) => fieldDef.field).map((fieldDef) => [fieldDef.field, fieldDef.displayName]),
      );
      chatbotDataFetchParams = { rows: { module, entity, ...rowsReq }, fieldToDisplayName };
    }
  }
  useChatbotRegistration(chatbotName, chatbotDescription, chatbotDataFetchParams);

  // First must fetch fieldDefs
  useEffect(() => {
    Promise.all([rsrc.fieldDefs(module, entity), rsrc.entityDef(module, entity)]).then(([fieldDefs, entityDef]) => {
      // If needed, augment the fieldDefs
      if (augFieldDefs) {
        const fieldToFieldDef = Object.fromEntries(fieldDefs.map((fieldDef) => [fieldDef.field, fieldDef]));
        augFieldDefs(fieldToFieldDef);
        fieldDefs = Object.values(fieldToFieldDef);
      }
      // Filter fieldDefs by permissions
      fieldDefs = EntityUtils.filterAuthorizedFields(module, entity, fieldDefs, getFieldRoleSet);
      // If this grid is showing the hist of an Entity, remove checkbox column
      if (hist) {
        fieldDefs = fieldDefs.filter((fieldDef) => !fieldDef.checkboxSelection);
      }
      fieldDefs.sort((a, b) => {
        // 1) checkbox field leads
        if (a.field === '' && b.field !== '') return -1;
        if (b.field === '' && a.field !== '') return 1;
        // 2) draggable field next
        if (a.rowDrag && !b.rowDrag) return -1;
        if (b.rowDrag && !a.rowDrag) return 1;
        // 3) then the self link (code, or name)
        if (a.type === 'SELF_LINK' && b.type !== 'SELF_LINK') return -1;
        if (b.type === 'SELF_LINK' && a.type !== 'SELF_LINK') return 1;
        // 4) finally, by numeric precedence (defaulting to 0)
        return (a.precedence || 0) - (b.precedence || 0);
      });
      setFieldDefs(fieldDefs);
      setEntityDef(entityDef);
    });
  }, []);

  const { refreshTrigger } = useEntityChangeSubscr(module, entity);

  useEffect(() => {
    if (!gridRef.current) return;
    refreshGrid();
  }, [refreshTrigger, refreshTriggerProp, augRowsReq, clientSideRowData]);

  const onGridReady = (params) => {
    if (params.api.getGridOption('rowModelType') === 'clientSide') {
      clientSideRowData((dataSource) => gridRef.current.setDataSource(dataSource));
    } else {
      serverSideDataSource();
    }
  };

  const serverSideDataSource = () => {
    gridRef.current.setRowCount(null);
    const dataSource = {
      getRows: (params) => {
        if (!gridRef.current) return;

        const rowsReq = buildRowsReq(params);
        buildAndSetExportReq(rowsReq);

        rsrc.rows(module, entity, rowsReq).then((rows) => {
          gridRef.current.onGridSuccess(rows, params);
          gridRef.current.resize();

          rsrc.rowCount(module, entity, rowsReq).then((count) => {
            gridRef.current.setRowCount(count);
          });
        });
      },
    };

    gridRef.current.setDataSource(dataSource);
  };

  const buildRowsReq = (params) => {
    const rowsReq = {};

    rowsReq.startRow = params.startRow;
    rowsReq.endRow = params.endRow;

    const paramExpr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

    const exprs = [paramExpr];

    // // AsOfDate
    // if (this.asOfDate) {
    //   exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', this.asOfDate))
    //   exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER', this.asOfDate))
    // }

    // History
    if (hist) {
      rowsReq.hist = true;
      exprs.push(ExprUtils.fieldExpr('id', 'IS', navState.entityId));
    }

    if (navState.expr) {
      exprs.push(navState.expr);
    }

    if (navState.query) {
      rowsReq.query = navState.query;
    }

    if (navState.replaceParams) {
      rowsReq.replaceParams = navState.replaceParams;
    }

    // Here we combine all the expressions
    rowsReq.expr = ExprUtils.and(...exprs);

    rowsReq.sortModel = params.sortModel;

    rowsReq.partition = navState.partition;

    if (augRowsReq) {
      augRowsReq(rowsReq);
    }

    return rowsReq;
  };

  const buildAndSetExportReq = (rowsReq) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module,
      entity,
      label: tabLabel,
      rowsReq,
      fieldDefs,
      uiType: 'LIST',
    };

    setExportReq(exportReq);
  };

  const refreshGrid = () => {
    if (gridRef.current.getRowModelType() === 'serverSide') {
      gridRef.current.gridApiRef.current.refreshServerSide({ purge: true });
    } else if (gridRef.current.getRowModelType() === 'clientSide') {
      clientSideRowData((dataSource) => gridRef.current.setDataSource(dataSource));
    } else {
      serverSideDataSource();
    }
  };

  const { entityObj } = useEntityMgr();
  const getContextMenuItems = (params) => {
    let navState = {
      mode: Mode.DETAIL,
      entityId: params.node.data.id,
    };
    if (entityObj?.partition) {
      navState.partition = entityObj.partition;
    }

    const contextMenuItems = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo(module + '/' + StringUtils.toKebabConvention(entity), navState),
      },
    ];

    if (hasRole(`${module.toUpperCase()}_${entity}_DELETE`)) {
      contextMenuItems.push({
        name: 'Delete',
        disabled: params.node.data.status === 'APPROVED' || params.node.data.status === 'APPROVING',
        icon: fas.faTrash,
        action: () => openConfirmDeleteDialog(params.node.data),
      });
    }

    return contextMenuItems;
  };

  const openConfirmDeleteDialog = (rowData) => {
    const data = {
      entity: entity,
      id: rowData.id,
      module: module,
    };
    openDialog(ConfirmDeleteDialog, { data });
  };

  /* Main buttons */
  const buttons = [];
  if (entityDef && entityDef.searchable) buttons.push(<SearchBox key="searchBox" />);
  buttons.push(<SubButton key="subButton" />, <AddButton key="addButton" />);
  if (augButtons) augButtons(buttons);

  /* Intra-tab buttons */
  const defaultTabButtons = [<AddButton entity={entity} />];
  if (augTabButtons) augTabButtons(defaultTabButtons);

  let renderButtons = tabLabel && tabLabel !== 'Info' ? defaultTabButtons : buttons;

  if (showExport) {
    renderButtons = [
      ...renderButtons,
      <ExportButton key="export" entityName={entityDef?.displayName} exportReq={exportReq} />,
    ];
  }

  if (mode === Mode.DETAIL && hist) {
    showTitle = false;
  }

  return (
    fieldDefs && (
      <ContentBox>
        {showTitle && (
          <>
            <EntityTitle buttons={renderButtons} />
            <EntityBanner />
          </>
        )}

        <Grid
          id={id ? id + 'Grid' : undefined}
          ref={gridRef}
          gridOptions={{
            fieldDefs: fieldDefs,
            getContextMenuItems: getContextMenuItems,
            rowModelType: clientSideRowData ? 'clientSide' : DEFAULT_GRID_OPTIONS.rowModelType,
            ...gridOptions,
          }}
          events={{
            ...events,
            onGridReady,
          }}
        />
      </ContentBox>
    )
  );
};
